<template>
  <!-- Edit a user from the super admin user list: name, phone, sign-in and workspaces (US 9436) -->
  <b-modal ref="panel"
           modal-class="us-drawer"
           hide-header
           hide-footer
           @hidden="reset"
  >
    <div v-if="user" class="us-drawer__inner" qid="admin-edit-user-panel">
      <header class="us-drawer__head">
        <span class="us-avatar us-avatar--lg" :style="{ backgroundImage: 'url(' + avatarUrl + ')' }"></span>
        <div class="us-drawer__who">
          <h2 class="us-drawer__name">{{ user.name }}</h2>
          <div class="us-drawer__meta">{{ user.email }}</div>
        </div>
        <button type="button"
                class="us-icon-btn us-drawer__close"
                :aria-label="$t('administration.users.create.cancel')"
                @click="close">
          <font-awesome-icon icon="times"/>
        </button>
      </header>

      <b-form class="us-drawer__body" qid="admin-edit-user-form" @submit.prevent="save">
        <b-alert v-if="error"
                 show
                 variant="danger"
                 qid="admin-edit-user-error">{{ error }}</b-alert>

        <section class="us-drawer__section">
          <div class="us-status">
            <template v-if="user.last_login_at">
              <span class="v2-tag v2-tag--teal"><font-awesome-icon icon="check"/>{{ $t('users_page.edit.signed_in') }}</span>
              <span class="us-status__text">{{ $t('users_page.edit.last_login', { date: formatDateTime(user.last_login_at) }) }}</span>
            </template>
            <template v-else>
              <span class="v2-tag v2-tag--amber">{{ $t('admin_page.users.activity.never') }}</span>
              <span class="us-status__text">{{ $t('admin_page.users.created', { date: formatDateTime(user.created_at) }) }}</span>
            </template>
          </div>
        </section>

        <section class="us-drawer__section">
          <h3 class="us-drawer__label">{{ $t('admin_page.users.edit.section_details') }}</h3>
          <div class="adm-fields">
            <label class="adm-field adm-field--wide">
              <span class="us-field-label">{{ $t('administration.users.create.name_label') }}</span>
              <b-form-input v-model="form.name" required qid="admin-edit-user-name"/>
            </label>
            <label class="adm-field adm-field--wide">
              <span class="us-field-label">{{ $t('administration.users.create.phone_label') }}</span>
              <b-form-input v-model="form.phone" qid="admin-edit-user-phone"/>
            </label>
          </div>
        </section>

        <section class="us-drawer__section">
          <h3 class="us-drawer__label">{{ $t('users_page.edit.section_sign_in') }}</h3>
          <SignInMethodPicker v-model="form.authentication"/>
          <b-form-checkbox v-if="switchesToPassword"
                           v-model="form.send_password_email"
                           qid="admin-edit-user-send-password-email"
          >
            {{ $t('users_page.edit.send_password_email') }}
          </b-form-checkbox>
          <b-form-group v-if="form.authentication === 'Microsoft'" class="mb-0">
            <label class="us-field-label">{{ $t('users_page.fields.upn') }}</label>
            <b-form-input v-model="form.upn_email"
                          type="email"
                          :maxLength="255"
                          :placeholder="user.email"
                          qid="admin-edit-user-upn"
            ></b-form-input>
            <small class="us-hint">{{ $t('users_page.fields.upn_hint') }}</small>
          </b-form-group>
        </section>

        <section class="us-drawer__section">
          <div class="adm-modules-head">
            <h3 class="us-drawer__label">{{ $t('admin_page.users.edit.section_workspaces') }}</h3>
            <span class="v2-tag v2-tag--grey">{{ workspaces.length }}</span>
          </div>
          <router-link v-for="workspace in workspaces"
                       :key="workspace.id"
                       :to="{ name: 'administration.clientView', params: { slug: workspace.slug } }"
                       class="adm-workspace"
                       @click.native="close"
          >
            <font-awesome-icon :icon="workspace.parent_name ? 'layer-group' : 'building'" fixed-width class="adm-workspace__icon"/>
            <span class="adm-workspace__name">
              {{ workspace.name }}
              <small v-if="workspace.parent_name">{{ $t('admin_page.overview.partition_of', { client: workspace.parent_name }) }}</small>
            </span>
            <font-awesome-icon icon="angle-right" class="mt-row__chevron"/>
          </router-link>
          <div v-if="!workspaces.length" class="us-muted">{{ $t('admin_page.users.no_workspaces') }}</div>
        </section>

        <div class="us-drawer__foot">
          <router-link :to="{ name: 'administration.userView', params: { slug: user.slug } }" class="us-link" @click.native="close">
            {{ $t('admin_page.users.open_profile') }}
          </router-link>
          <span class="us-grow"></span>
          <b-button variant="secondary" qid="admin-edit-user-cancel-button" @click="close">
            {{ $t('administration.users.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    variant="success"
                    :disabled="saving"
                    qid="admin-edit-user-submit-button"
          >
            <font-awesome-icon v-if="saving" icon="spinner" class="fa-spin mr-1"/>
            {{ $t('administration.users.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </div>
  </b-modal>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import {config} from '@/config.js';
import {userAvatarUrl} from '@/util/userAvatar.js';
import SignInMethodPicker from '@/components/user/SignInMethodPicker.vue';

export default {
  name: 'AdminUserEditPanel',
  components: {
    SignInMethodPicker
  },
  data() {
    return {
      user: null,
      form: {},
      error: '',
      saving: false
    }
  },
  computed: {
    avatarUrl() {
      return userAvatarUrl(this.user, true);
    },
    workspaces() {
      return (this.user && this.user.workspaces) || [];
    },
    // Moving someone from Microsoft or Google to a password: offer to send a set-password email
    switchesToPassword() {
      let from = this.user.authentication || 'Application';
      return from !== 'Application' && this.form.authentication === 'Application';
    }
  },
  methods: {
    open(user) {
      this.user = Object.assign({}, user);
      this.form = {
        name: user.name,
        phone: user.phone || '',
        authentication: user.authentication || 'Application',
        upn_email: user.upn_email || '',
        send_password_email: true
      };
      this.error = '';
      this.$refs.panel.show();
    },
    close() {
      this.$refs.panel.hide();
    },
    reset() {
      this.user = null;
      this.saving = false;
    },
    formatDateTime(date) {
      return date ? this.$options.filters.formatDateTime(date, config.dateFormat + ' HH:mm') : '';
    },
    save() {
      let self = this;
      this.saving = true;
      this.error = '';

      let data = {
        name: this.form.name,
        phone: this.form.phone,
        authentication: this.form.authentication,
        send_password_email: this.switchesToPassword && this.form.send_password_email ? 1 : 0
      };
      // The UPN only matters for Microsoft sign-in; other methods leave it as it is
      if (this.form.authentication === 'Microsoft') {
        data.upn_email = this.form.upn_email;
      }

      piincHttp.put('users/' + this.user.slug, data).then(function() {
        self.saving = false;
        self.$toastr('success', self.$t('administration.users.update.success'));
        self.$emit('saved');
        self.close();
      }, function(error) {
        self.saving = false;
        self.error = (error.data && error.data.message) || self.$t('admin_page.error');
      });
    }
  }
}
</script>
